import React, { useState, useEffect } from "react";
import { uploadToCloudinary } from "../../functions/Cloudinary";
import { getFirestore, doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { getApp } from "firebase/app";

const DEFAULT_CLOUD_NAME = "dn58a8in7";
const DEFAULT_UPLOAD_PRESET = "travelscape";

const ALL_IMAGES_DOC_PATH = ["gallery", "allimages"]; 

const GalleryImage = () => {
  const [cloudName, setCloudName] = useState(DEFAULT_CLOUD_NAME);
  const [uploadPreset, setUploadPreset] = useState(DEFAULT_UPLOAD_PRESET);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [captions, setCaptions] = useState([]); // Array of captions for selected files
  const [uploadedImages, setUploadedImages] = useState([]); // [{url, createdAt}]
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [previewUrls, setPreviewUrls] = useState([]);

  // Fetch images from Firestore on mount
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const db = getFirestore(getApp());
        const docRef = doc(db, ...ALL_IMAGES_DOC_PATH);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          // Sort by createdAt descending if available
          const images = docSnap.data().images || [];
          images.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setUploadedImages(images);
        } else {
          setUploadedImages([]);
        }
      } catch (err) {
        console.error("Error fetching images from Firestore:", err);
      }
    };
    fetchImages();
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setMessage({ text: "", type: "" });

    // Generate previews
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);

    // Initialize captions array with empty strings
    setCaptions(files.map(() => ""));
  };

  const handleCaptionChange = (idx, value) => {
    setCaptions(prev => {
      const updated = [...prev];
      updated[idx] = value;
      return updated;
    });
  };

  const handleRemoveSelected = (idx) => {
    const newFiles = selectedFiles.filter((_, i) => i !== idx);
    const newPreviews = previewUrls.filter((_, i) => i !== idx);
    const newCaptions = captions.filter((_, i) => i !== idx);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
    setCaptions(newCaptions);
  };

  // Save image with caption
  const saveImageUrlToFirestore = async (url, caption) => {
    try {
      const db = getFirestore(getApp());
      const docRef = doc(db, ...ALL_IMAGES_DOC_PATH);
      const imageObj = { url, caption, createdAt: Date.now() };
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        await setDoc(docRef, { images: [imageObj] });
      } else {
        await updateDoc(docRef, { images: arrayUnion(imageObj) });
      }
      return imageObj;
    } catch (err) {
      return null;
    }
  };

  const handleUpload = async () => {
    if (!cloudName || !uploadPreset || selectedFiles.length === 0) {
      setMessage({ text: "Please select files and enter Cloudinary details.", type: "danger" });
      return;
    }
    // Check that all captions are filled
    if (captions.some(caption => caption.trim() === "")) {
      setMessage({ text: "Please enter a caption for each image.", type: "danger" });
      return;
    }
    setIsUploading(true);
    setMessage({ text: "Uploading...", type: "info" });
    try {
      const newImages = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const caption = captions[i];
        const url = await uploadToCloudinary(file, cloudName, uploadPreset, "Gallery");
        if (!url) continue;
        const saved = await saveImageUrlToFirestore(url, caption);
        if (saved) newImages.push(saved);
      }
      setUploadedImages(prev => [...newImages, ...prev]);
      setSelectedFiles([]);
      setPreviewUrls([]);
      setCaptions([]);
      setMessage({ text: "Upload successful!", type: "success" });
    } catch (err) {
      setMessage({ text: "Upload failed.", type: "danger" });
    } finally {
      setIsUploading(false);
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    }
  };

  const handleDelete = async (url) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    try {
      const db = getFirestore(getApp());
      const docRef = doc(db, ...ALL_IMAGES_DOC_PATH);
      // Find the image object to remove (by url)
      const imageToRemove = uploadedImages.find(img => img.url === url);
      if (!imageToRemove) return;
      await updateDoc(docRef, { images: arrayRemove(imageToRemove) });
      setUploadedImages(prev => prev.filter(img => img.url !== url));
      setMessage({ text: "Image deleted.", type: "success" });
      setTimeout(() => setMessage({ text: "", type: "" }), 2000);
    } catch (err) {
      setMessage({ text: "Failed to delete image.", type: "danger" });
    }
  };

  return (
    <div className="gallery-image-section fadeIn">
      <div className="admin-card mb-4">
        <div className="card-header">
          <h3>Gallery Image Upload</h3>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6 mb-2">
              <label className="form-label">Cloud Name</label>
              <input
                type="text"
                className="form-control"
                value={cloudName}
                onChange={e => setCloudName(e.target.value)}
                placeholder="Cloudinary cloud name"
              />
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label">Upload Preset</label>
              <input
                type="text"
                className="form-control"
                value={uploadPreset}
                onChange={e => setUploadPreset(e.target.value)}
                placeholder="Cloudinary upload preset"
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Select Images</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </div>
          {/* Preview selected images with caption input and remove icon */}
          {previewUrls.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {previewUrls.map((url, idx) => (
                <div key={url} className="position-relative" style={{ width: 120 }}>
                  <img
                    src={url}
                    alt={`Selected ${idx + 1}`}
                    className="img-thumbnail"
                    style={{ width: 80, height: 80, objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                    style={{ zIndex: 2, padding: "2px 6px" }}
                    onClick={() => handleRemoveSelected(idx)}
                    title="Remove"
                  >
                    &times;
                  </button>
                  <input
                    type="text"
                    className="form-control form-control-sm mt-2"
                    placeholder="Enter caption"
                    value={captions[idx] || ""}
                    onChange={e => handleCaptionChange(idx, e.target.value)}
                    disabled={isUploading}
                  />
                </div>
              ))}
            </div>
          )}
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
          >
            {isUploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                Uploading...
              </>
            ) : (
              <>Upload</>
            )}
          </button>
          {message.text && (
            <div className={`alert alert-${message.type} mt-3`}>{message.text}</div>
          )}
        </div>
      </div>
      <div className="admin-card">
        <div className="card-header">
          <h4>Uploaded Images</h4>
        </div>
        <div className="card-body">
          {uploadedImages.length === 0 ? (
            <div className="text-muted">No images uploaded yet.</div>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {uploadedImages.map((img, idx) => (
                <div key={img.url} className="position-relative" style={{ width: 140 }}>
                  <img
                    src={img.url}
                    alt={img.caption || `Gallery ${idx + 1}`}
                    className="img-thumbnail"
                    style={{ width: 120, height: 120, objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                    style={{ zIndex: 2 }}
                    onClick={() => handleDelete(img.url)}
                    title="Delete"
                  >
                    &times;
                  </button>
                  {/* Show caption below image */}
                  <div className="small text-center mt-1 text-muted" style={{ maxWidth: 120, wordBreak: "break-word" }}>
                    {img.caption}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GalleryImage;
